import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle, ArrowRight, Loader2 } from 'lucide-react';
import { useSubscription } from '@/hooks/useSubscription';
import { supabase } from '@/integrations/supabase/client';

type PlanKey = 'free' | 'pro';

const LandingPricing = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isPaymentsEnabled } = useSubscription();
  const [loadingPlan, setLoadingPlan] = useState<PlanKey | null>(null);

  const free = [t('landing.planFreeFeat1'), t('landing.planFreeFeat2'), t('landing.planFreeFeat3'), t('landing.planFreeFeat4')];
  const pro = [t('landing.planProFeat1'), t('landing.planProFeat2'), t('landing.planProFeat3'), t('landing.planProFeat4'), t('landing.planProFeat5')];

  // Both states must look visually identical — only the badge text and
  // button behavior differ.
  const ctaLabel = isPaymentsEnabled
    ? t('landing.ctaStartTrial', { defaultValue: 'Start Free Trial' })
    : t('landing.ctaJoinFree', { defaultValue: 'Join Free – No Credit Card Needed' });

  const badgeLabel = isPaymentsEnabled
    ? t('landing.badgeTrial', { defaultValue: '14-day free trial included' })
    : t('landing.badgeBeta', { defaultValue: 'Free During Beta' });

  const handleCta = async (plan: PlanKey, tier: string) => {
    if (!isPaymentsEnabled) {
      navigate('/auth');
      return;
    }

    setLoadingPlan(plan);
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: { tier },
      });

      const errorCode = (() => {
        if (!error) return null;
        const ctxBody = (error as any)?.context?.body;
        if (typeof ctxBody === 'string') return ctxBody;
        if (ctxBody?.error) return String(ctxBody.error);
        const msg = (error as any)?.message;
        return typeof msg === 'string' ? msg : null;
      })();

      const dataErrorCode = (data as any)?.error ? String((data as any).error) : null;
      const code = errorCode || dataErrorCode;

      if (code && (code.includes('PAYMENTS_DISABLED') || code.includes('STRIPE_NOT_CONFIGURED'))) {
        // Silent fallback — payments not actually live yet.
        navigate('/auth');
        return;
      }

      if (error) {
        toast.error(
          t('landing.toastCheckoutFailed', {
            defaultValue: 'Could not start checkout. Please try again.',
          }),
        );
        setLoadingPlan(null);
        return;
      }

      const url = (data as any)?.url;
      if (url) {
        window.location.href = url;
        return;
      }

      toast.error(
        t('landing.toastCheckoutFailed', {
          defaultValue: 'Could not start checkout. Please try again.',
        }),
      );
      setLoadingPlan(null);
    } catch {
      toast.error(
        t('landing.toastCheckoutFailed', {
          defaultValue: 'Could not start checkout. Please try again.',
        }),
      );
      setLoadingPlan(null);
    }
  };

  return (
    <section className="py-20 sm:py-28 px-4 bg-muted/30">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-sm font-semibold text-primary uppercase tracking-wider mb-2">{t('landing.pricingSubtitle')}</p>
          <h2 className="text-3xl sm:text-4xl font-bold">{t('landing.pricingTitle')}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Free */}
          <Card className="border-border/50">
            <CardContent className="p-8 space-y-6">
              <div>
                <div className="mb-2">
                  <span className="inline-block text-[11px] font-semibold uppercase tracking-wider text-primary bg-primary/10 border border-primary/20 px-2.5 py-1 rounded-full">
                    {badgeLabel}
                  </span>
                </div>
                <h3 className="text-xl font-bold">{t('landing.planFreeTitle')}</h3>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="text-4xl font-bold">{t('landing.planFreePrice')}</span>
                  <span className="text-sm text-muted-foreground">{t('landing.planFreePriceLabel')}</span>
                </div>
              </div>
              <ul className="space-y-3">
                {free.map((f, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm">
                    <CheckCircle className="h-4 w-4 text-primary shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <Button
                variant="outline"
                className="w-full rounded-xl h-11"
                onClick={() => handleCta('free', 'free')}
                disabled={loadingPlan === 'free'}
              >
                {loadingPlan === 'free' && <Loader2 className="h-4 w-4 me-2 animate-spin" />}
                {ctaLabel}
              </Button>
            </CardContent>
          </Card>
          {/* Pro */}
          <Card className="border-primary/50 shadow-lg shadow-primary/10 relative">
            <div className="absolute -top-3 start-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-bold px-4 py-1 rounded-full">
              {t('landing.planProBadge')}
            </div>
            <CardContent className="p-8 space-y-6">
              <div>
                <div className="mb-2">
                  <span className="inline-block text-[11px] font-semibold uppercase tracking-wider text-primary bg-primary/10 border border-primary/20 px-2.5 py-1 rounded-full">
                    {badgeLabel}
                  </span>
                </div>
                <h3 className="text-xl font-bold">{t('landing.planProTitle')}</h3>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="text-4xl font-bold">{t('landing.planProPrice')}</span>
                  <span className="text-sm text-muted-foreground">{t('landing.planProPriceLabel')}</span>
                </div>
              </div>
              <ul className="space-y-3">
                {pro.map((f, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm">
                    <CheckCircle className="h-4 w-4 text-primary shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <Button
                className="w-full rounded-xl h-11 shadow-lg shadow-primary/25"
                onClick={() => handleCta('pro', 'family')}
                disabled={loadingPlan === 'pro'}
              >
                {loadingPlan === 'pro' && <Loader2 className="h-4 w-4 me-2 animate-spin" />}
                {ctaLabel}
                {loadingPlan !== 'pro' && <ArrowRight className="ms-2 h-4 w-4" />}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default LandingPricing;
